/**
 * 用户鉴权 token 统一读写
 * - localStorage.user_token：普通登录持久凭证（关浏览器仍在）
 * - sessionStorage.user_token：后台跳转临时凭证（关标签页即失效）
 * - 同时存在时，优先使用 sessionStorage
 */

const TOKEN_KEY = 'user_token'

/**
 * 从一段查询串里取出指定参数（不用 URLSearchParams，兼容性更好）
 * @param {string} queryString 如 ?a=1&user_token=xxx 或 a=1&user_token=xxx
 * @param {string} key 参数名
 * @returns {string|null}
 */
function pickQueryValue(queryString, key) {
  if (!queryString) return null
  // 去掉开头的 ?
  const raw = queryString.charAt(0) === '?' ? queryString.slice(1) : queryString
  const parts = raw.split('&')
  for (let i = 0; i < parts.length; i++) {
    const eq = parts[i].indexOf('=')
    if (eq === -1) continue
    try {
      const name = decodeURIComponent(parts[i].slice(0, eq))
      if (name === key) {
        // JWT 可能含 + / = ，需要 decode；非法 % 编码时走 catch 用原始值
        return decodeURIComponent(parts[i].slice(eq + 1))
      }
    } catch (e) {
      // decodeURIComponent 遇到非法 % 会抛错，不能让整站起不来
      const name = parts[i].slice(0, eq)
      if (name === key) {
        return parts[i].slice(eq + 1)
      }
    }
  }
  return null
}

/**
 * 从当前页面地址解析后台跳转带来的 user_token
 * history：/user/index?user_token=xxx
 * hash 兜底：#/user/index?user_token=xxx
 * @returns {string|null}
 */
export function getUrlUserToken() {
  if (typeof window === 'undefined') return null
  // history 模式查询串在 location.search
  const fromSearch = pickQueryValue(window.location.search, TOKEN_KEY)
  if (fromSearch) return fromSearch
  // hash 模式：参数在 # 后面
  const hash = window.location.hash || ''
  const qIndex = hash.indexOf('?')
  if (qIndex === -1) return null
  return pickQueryValue(hash.slice(qIndex), TOKEN_KEY)
}

/**
 * 获取当前生效的 token
 * 优先级：sessionStorage > localStorage
 * 没有时返回 null（与原先 localStorage.getItem 行为一致，避免 == null 判断失效）
 * @returns {string|null}
 */
export function getToken() {
  // 后台跳转的临时 token 优先，避免被本地旧登录覆盖
  return (
    sessionStorage.getItem(TOKEN_KEY) ||
    localStorage.getItem(TOKEN_KEY) ||
    null
  )
}

/**
 * 是否存在可用 token（session 或 local 任一有即可）
 * @returns {boolean}
 */
export function hasToken() {
  return !!getToken()
}

/**
 * 当前标签是否存在后台跳转的 session token
 * @returns {boolean}
 */
export function hasSessionToken() {
  return !!sessionStorage.getItem(TOKEN_KEY)
}

/**
 * 将后台跳转链接上的 token 写入 sessionStorage
 * 关闭页面/标签后自动消失，不影响本地正式登录
 * @param {string} token
 */
export function setSessionToken(token) {
  if (!token) return
  sessionStorage.setItem(TOKEN_KEY, token)
}

/**
 * 从当前 URL 捕获 user_token 并写入 sessionStorage
 * 必须在 Vue / 路由守卫之前执行，否则 App.vue created 可能先走到「无 token」把本地登录清掉
 * @returns {string|null} 捕获到的 token
 */
export function captureUrlToken() {
  const token = getUrlUserToken()
  if (token) {
    setSessionToken(token)
  }
  return token
}

/**
 * 普通登录写入 localStorage，并清掉 session 临时 token
 * 避免 session 仍优先导致新登录不生效
 * @param {string} token
 */
export function setLocalToken(token) {
  if (!token) return
  sessionStorage.removeItem(TOKEN_KEY)
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * 清除当前生效的 token
 * 有 session 只清 session（可回退到本地正式登录）；否则清 local
 */
export function removeToken() {
  if (sessionStorage.getItem(TOKEN_KEY)) {
    sessionStorage.removeItem(TOKEN_KEY)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

/**
 * 退出登录：同时清除 session 与 local 中的 token
 */
export function clearAllToken() {
  sessionStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(TOKEN_KEY)
}

// 文件一被引用就先从地址栏捞 token，保证后续 getToken / axios / App.vue 都能读到
captureUrlToken()
